import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, HelperService, OrderService, UserService, WindowService, buildProfileSearchFields, environment } from "@mypxplat/xplat/core";
import { Router, ActivatedRoute } from "@angular/router";
import { AppService, AvoService, BetaService, WebCommunityService, WebErrorHandler } from "@mypxplat/xplat/web/core";
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { NgbDateStruct, NgbDate } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import {
  EditAddressComponent,
  UploadFileComponent,
  DownloadDataComponent,
  ChangePasswordComponent,
  DeleteDataComponent,
  VerifyEmailComponent,
  DatePickerComponent,
  LinkFenderAccountComponent,
} from "../../../modals";
import { catchError, filter, take } from "rxjs/operators";
import { of } from "rxjs";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore } from "@angular/fire/compat/firestore";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-account-general",
  templateUrl: "./general.component.html",
  standalone: false,
})
export class GeneralComponent extends BaseComponent implements OnInit {
  @ViewChild("captchaModal") captchaModal;
  public maxDate: NgbDateStruct;
  public minDate: NgbDateStruct;
  public birthday: Date;
  public saving: boolean = false;
  /** Blocks the page with the wave loader while an email change fans out server-side. */
  public changingEmail: boolean = false;
  public saveButtonText: string;
  public env = environment;
  public languages: Array<any>;
  public countries: Array<any>;

  public originalUser: any;

  public availableSkills: Array<any>;
  public mySkills: Array<any> = [];
  public fbUser: any;
  public isPublic: boolean = false;
  public tagline: string = "";

  public addresses: Array<any> = [];
  public captcha: string = "";

  public uploadingDeveloperKey: boolean = false;
  public developerKeyMessage: string = "";
  public developerKeyError: boolean = false;
  public isExternalDeveloper: boolean = false;

  constructor(
    public userService: UserService,
    public helperService: HelperService,
    private _win: WindowService,
    private _router: Router,
    private _route: ActivatedRoute,
    public appService: AppService,
    private _sanitizer: DomSanitizer,
    private _translate: TranslateService,
    public fbAuth: AngularFireAuth,
    public db: AngularFirestore,
    public router: Router,
    public communityService: WebCommunityService,
    public errorService: WebErrorHandler,
    public orderService: OrderService,
    private avoService: AvoService,
    private _betaService: BetaService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.originalUser = { ...this.user };
    this.init();
    this.communityService.getUserProfile(this.user.id).then((profile) => {
      this.fbUser = profile;

      this.isPublic = this.fbUser?.public ? true : false;
      this.tagline = this.fbUser?.tagline ? this.fbUser.tagline + "" : "";
    });

    const value: any = this.user?.external_developer;
    this.isExternalDeveloper = value === true || value === 1 || value === "1";

    if (this._route.snapshot.queryParams["changePassword"] === "true") {
      this._win.setTimeout(() => {
        this.openChangePassword();
        this._router.navigate([], {
          queryParams: { changePassword: null },
          queryParamsHandling: "merge",
        });
      }, 0);
    }
  }

  init() {
    this._win.setTimeout(() => {}, 500);
    this._translate.get("general.save").subscribe((string) => (this.saveButtonText = string));
    const year = parseInt(moment().format("YYYY"));
    const month = parseInt(moment().format("M"));
    const day = parseInt(moment().format("D"));
    // this.minDate = new NgbDate(1900, 1, 1);
    // this.maxDate = new NgbDate(parseInt(moment().subtract(13, "years").format("YYYY")), parseInt(moment().subtract(13, "years").format("M")), parseInt(moment().subtract(13, "years").format("D")));
    if (this.user.birthDay) {
      this.birthday = new Date();
      this.birthday.setDate(this.user.birthDay);
      this.birthday.setMonth(this.user.birthMonth - 1);
      this.birthday.setFullYear(this.user.birthYear);
    }
    this.userService.listLanguages().subscribe((result: any) => {
      this.languages = result;
    });
    this.userService.listCountries().subscribe((result) => {
      this.countries = [];
      for (var i in result) {
        this.countries.push({
          title: result[i],
          code: i,
        });
      }
    });
  }

  showDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalInstance.componentInstance.initWithDate = this.birthday;
    modalInstance.componentInstance.earliestYear = 1920;
    modalInstance.componentInstance.dateSelected.subscribe((date) => {
      this.birthday = date;
    });
  }

  makeDefaultAddress(address_id) {
    this.userService.setDefaultAddress(address_id).subscribe((result: any) => {
      if (result.success) {
        this.addresses.forEach((ad) => {
          if (ad.id == address_id) {
            ad.main_address = "1";
          } else {
            ad.main_address = null;
          }
        });
      }
    });
  }

  removeCreditCard(card) {
    if (confirm(this._translate.instant("sphere.account.remove_card_confirm"))) {
      let data = {
        paymentSources: {
          creditCardInfo: [
            {
              creditCard: {
                cardType: card["credit-card"]["card-type"],
                cardLastFourDigits: card["credit-card"]["card-last-four-digits"],
              },
              status: "D",
            },
          ],
        },
      };
      card.loading = true;
      this.userService.updateCreditCardDetails(data).subscribe((result) => {
        card.loading = false;
        this.userService.getUserDetails(false).subscribe((result) => {
          this.init();
        });
      });
    }
  }

  deleteAddress(address_id: any) {
    if (confirm("Are you sure you would like to remove this address? This cannot be undone.")) {
      this.userService.deleteAddress(address_id).subscribe({
        next: (result: any) => {
          this.userService.getAddresses().subscribe({
            next: (result: any) => {
              this.addresses = result;
            },
          });
        },
        error: (error: any) => {
          this.appService.alertError(error);
        },
      });
    }
  }

  editAddress(address: any) {
    const modalRef = this.appService.showModal(EditAddressComponent, { size: "sm" });
    modalRef.componentInstance.address = address || {};
    modalRef.componentInstance.addressAdded.subscribe((result) => {
      if (result) {
        this.userService.getAddresses().subscribe({
          next: (result: any) => {
            this.addresses = result;
          },
        });
      }
    });
  }

  showChangeProfilePhoto() {
    const modalRef = this.appService.showModal(UploadFileComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.uploadMethod = "change_profile_photo";
    modalRef.componentInstance.allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
    modalRef.componentInstance.cropRatio = 1;
    modalRef.componentInstance.fileUploaded.subscribe((result) => {
      if (result.user) {
        this.communityService.updateProfile({ photo: result.user.photoURL + "?" + moment().unix() }).then((updateRes) => {
          this.communityService.refreshUserProfile(result.user.id);
        });
      }
    });
  }

  showChangeCoverPhoto() {
    const modalRef = this.appService.showModal(UploadFileComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.uploadMethod = "change_cover_photo";
    modalRef.componentInstance.allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
    modalRef.componentInstance.cropRatio = 1;
    modalRef.componentInstance.fileUploaded.subscribe((result) => {
      if (result.user) this.communityService.updateProfile({ cover_photo: result.user.cover_photo });
    });
  }

  removeProfilePhoto() {
    this.appService.confirm("Are you sure you would like to remove your profile photo? This cannot be undone.").then((confirmed) => {
      if (!confirmed) return;

      // Clear it locally before the requests go out. Removing a photo is two round trips with no
      // progress UI of its own, and `photoURL` backs both the avatar here and the one in the header,
      // so blanking it is what makes the click feel like it did something. Restored if the call fails.
      const previousPhoto = this.userService.user.photoURL;
      this.userService.user.photoURL = null;

      this.userService.deletePhoto().subscribe({
        next: (result: any) => {
          if (result.success) {
            this.communityService.updateProfile({ photo: null }).then(() => {
              this.communityService.refreshUserProfile(this.user.id);
            });
          } else {
            this.restoreProfilePhoto(previousPhoto);
          }
        },
        error: () => this.restoreProfilePhoto(previousPhoto),
      });
    });
  }

  private restoreProfilePhoto(previousPhoto: string) {
    this.userService.user.photoURL = previousPhoto;
    this.appService.alert("Sorry, we could not remove your profile photo. Please try again.");
  }

  removeCoverPhoto() {
    this.appService.confirm("Are you sure you would like to remove your cover photo? This cannot be undone.").then((confirmed) => {
      if (!confirmed) return;

      const previousCoverPhoto = this.user.cover_photo;
      this.user.cover_photo = null;

      this.userService.deletePhoto("cover_photo").subscribe({
        next: (result: any) => {
          if (result.success) {
            this.communityService.updateProfile({ cover_photo: null }).then(() => {
              this.communityService.refreshUserProfile(this.user.id);
            });
          } else {
            this.restoreCoverPhoto(previousCoverPhoto);
          }
        },
        error: () => this.restoreCoverPhoto(previousCoverPhoto),
      });
    });
  }

  private restoreCoverPhoto(previousCoverPhoto: string) {
    this.user.cover_photo = previousCoverPhoto;
    this.appService.alert("Sorry, we could not remove your cover photo. Please try again.");
  }

  downloadData() {
    // Fire CTA Clicked for Account Actions - Download Data (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Account Actions" });
            const extra: Record<string, any> = {
              ctaText: "Download Data",
              ctaLocation: "Account Actions",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    const modalRef = this.appService.showModal(DownloadDataComponent, { size: "lg", ariaLabelledBy: "modal-title" });
  }

  deleteData() {
    // Fire CTA Clicked for Account Actions - Delete Data (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Account Actions" });
            const extra: Record<string, any> = {
              ctaText: "Delete Data",
              ctaLocation: "Account Actions",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    const modalRef = this.appService.showModal(DeleteDataComponent, { size: "lg", ariaLabelledBy: "modal-title" });
  }

  openChangePassword() {
    // Fire CTA Clicked for Account Actions - Change Password (only in My Fender)
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof (te as any).ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Account Actions" });
            const extra: Record<string, any> = {
              ctaText: "Change Password",
              ctaLocation: "Account Actions",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            (te as any).ctaClicked(payload);
          }
        });
    }
    const modalRef = this.appService.showModal(ChangePasswordComponent, { size: "sm", ariaLabelledBy: "modal-title" });
  }

  public newPassword: string = "";
  public removingSocialLogin: boolean = false;
  removeSocialLogin() {
    this.removingSocialLogin = true;
    if (!this.userService.passwordTester.test(this.newPassword)) {
      alert(this._translate.instant("login.password_validation_12_chars"));
      this.removingSocialLogin = false;
      return;
    }
    this.appService.closeModals();
    this.userService.removeSocialLogin(this.newPassword).subscribe({
      next: (result) => {
        alert("Social login was successfully removed.");
        this.userService.getUserDetails(false).subscribe((result) => {
          this.removingSocialLogin = false;
          this.init();
        });
      },
      error: (error) => {
        this.appService.alertError(error, true);
        this.removingSocialLogin = false;
      },
    });
  }

  private _nameChangeDate(iso: string | null | undefined): string {
    return iso ? moment(iso).format("MMMM D, YYYY") : "";
  }

  /**
   * Resolves true when the save may go ahead. Only asks when the name posts display actually
   * changes; blocks without a request when the 30-day community limit is still running.
   */
  private _confirmCommunityNameChange(updateArgs: any): Promise<boolean> {
    const display = (first, last) => `${first ?? ""} ${last ?? ""}`.trim();
    if (display(this.originalUser.firstName, this.originalUser.lastName) === display(updateArgs.firstName, updateArgs.lastName)) {
      return Promise.resolve(true);
    }
    const info = (this.user as any).community_name_change;
    if (info?.locked) {
      this.appService.alert(this._translate.instant("account.community_name.locked", { last: this._nameChangeDate(info.last_changed_at), next: this._nameChangeDate(info.next_allowed_at) }));
      this.restoreSavedValues();
      return Promise.resolve(false);
    }
    const key = this._betaService.enrolledBetas?.length ? "account.community_name.confirm_with_beta" : "account.community_name.confirm";
    return this.appService.confirm(this._translate.instant(key), this._translate.instant("account.community_name.confirm_button")).then((ok) => !!ok);
  }

  saveAccountInfo() {
    const go = (updatingEmail) => {
      this.saving = true;
      // An email change fans out across Cognito, both Stripe accounts, Iterable, Zendesk,
      // Shopify and Firestore before it returns, so it is slow enough that a button label
      // reading "Saving..." is easy to miss — and navigating away mid-flight would strand the
      // account between systems. Block the page until it comes back.
      this.changingEmail = updatingEmail;

      this._translate.get("general.saving_dots").subscribe((string) => (this.saveButtonText = string));
      this.userService.saveUser(updateArgs).subscribe({
        next: (result) => {
          if (result.success) {
            if (
              updateArgs.email != this.originalUser.email ||
              updateArgs.firstName != this.originalUser.firstName ||
              updateArgs.lastName != this.originalUser.lastName ||
              updateArgs.description != this.originalUser.description ||
              this.fbUser?.public != this.isPublic ||
              this.fbUser?.tagline != this.tagline
            ) {
              let fbUpdateArgs: any = {
                email: updateArgs.email,
                firstName: updateArgs.firstName,
                lastName: updateArgs.lastName,
                // Search keys for the directory and @mention search. Derived in one
                // place so they can't drift apart or be skipped.
                ...buildProfileSearchFields({ firstName: updateArgs.firstName, lastName: updateArgs.lastName }),
                description: updateArgs.description,
                public: this.isPublic,
                tagline: this.tagline,
              };
              if (this.user.photoURL) fbUpdateArgs.photo = this.user.photoURL;
              if (this.user.cover_photo) fbUpdateArgs.cover_photo = this.user.cover_photo;
              this.communityService.updateProfile(fbUpdateArgs).then((result) => {});
            }

            // On an email change paeapi now syncs Shopify itself. Doing it from here as well
            // would race the sign-out below, which can cut the request off mid-flight.
            if (!updatingEmail && (updateArgs.firstName || updateArgs.lastName) && this.orderService.fenderUserShopData) {
              this.orderService
                .updateFenderShopifyUser(this.originalUser.email, "FENDER_US", {
                  firstName: updateArgs.firstName,
                  lastName: updateArgs.lastName,
                  email: updateArgs.email,
                })
                .subscribe();
            }

            // Re-baseline, so the next save on this page compares against what was just stored
            // (otherwise a completed rename keeps reading as a pending one and trips the lock).
            this.originalUser = { ...this.originalUser, ...updateArgs };

            if (updatingEmail) {
              // The email is the login identifier, so every existing session is now
              // authenticated against an identity that no longer resolves. Mirror the
              // change-password flow: revoke server-side, tell the user, then log out.
              this.userService
                .signOutCognitoUserGlobally()
                .pipe(catchError(() => of(null)))
                .subscribe(() => {
                  this.saving = false;
                  this.changingEmail = false;
                  this.handleEmailChanged();
                });
              return;
            }

            if (result.communityRename?.queued && result.communityRename?.cooldown_applied) {
              this.appService.alert(this._translate.instant("account.community_name.queued"));
            }
            this.userService.getUserDetails();
            this._translate.get("general.saved").subscribe((string) => (this.saveButtonText = string));
            this._win.setTimeout(() => {
              this.saving = false;
              this._translate.get("general.save").subscribe((string) => (this.saveButtonText = string));
            }, 1000);
          } else {
            this.saving = false;
            this.changingEmail = false;
            alert(result.message || result.error);
            this.restoreSavedValues();
          }
        },
        error: (error) => {
          this.saving = false;
          this.changingEmail = false;
          if (error?.error?.reason === "community_name_migration_required" && this.userService.needsToLinkFenderAccount) {
            this.appService.showModal(LinkFenderAccountComponent, { size: "lg" });
          } else {
            this.appService.alertError(error);
          }
          this.restoreSavedValues();
        },
      });
    };

    let updateArgs: any = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      gender: this.user.gender,
      phone: this.user.phone,
      webSiteURL: this.user.webSiteURL,
      description: this.user.description,
      public: this.isPublic,
      native_language: this.user.native_language,
      country: this.user.country,
    };

    if (this.birthday) {
      updateArgs.birthMonth = this.birthday.getMonth() + 1;
      updateArgs.birthYear = this.birthday.getFullYear();
      updateArgs.birthDay = this.birthday.getDate();
    }

    if (!updateArgs.firstName || !updateArgs.lastName || !updateArgs.email) {
      alert("Please fill out all the required information.");
    } else if (!this.saving) {
      this._confirmCommunityNameChange(updateArgs).then((proceed) => {
        if (!proceed) return;
        let updatingEmail = this.originalUser.email != updateArgs.email;
        if (updatingEmail) {
          // Deactivation on email change is now applied server-side. `active` gates an entitlement,
          // so it is no longer accepted from the client at all — sending it here would fail the
          // whole update as an uneditable field.
          this.captchaModalRef = this.appService.showModal(this.captchaModal, { size: "sm" });
          this.captchaModalRef.result
            .then((result) => {
              this.userService.verifyCaptcha({ captcha: this.captcha }).subscribe({
                next: (result) => {
                  go(true);
                },
                error: (error) => {
                  alert("Verifying the captcha failed. Please try again.");
                },
              });
            })
            .catch((error) => {
              // console.log(error);
            });
        } else {
          go(false);
        }
      });
    }
  }

  /**
   * Repaints the form with what is actually stored, after a save fails.
   *
   * The inputs are two-way bound straight onto `user`, so a rejected save leaves the value the
   * user typed sitting in the field — it reads as though it saved. Re-fetching rather than
   * reverting to the `originalUser` snapshot is deliberate: some failures happen *after* the
   * database write (the Fender user-service call is not atomic with it), and in that case the
   * snapshot would be just as wrong as the typed value. The refreshed user is pushed through
   * `user$`, which BaseComponent is subscribed to, so the bound fields update themselves.
   */
  private restoreSavedValues() {
    this.userService.getUserDetails(false).subscribe({
      next: (result: any) => {
        // Re-baseline, so the next save compares against what is really stored.
        this.originalUser = { ...result };
      },
      error: () => {},
    });
  }

  /**
   * Tells the user their email moved, then logs them out once they acknowledge it. Same shape as
   * ChangePasswordComponent.handlePasswordChanged so both credential changes behave identically.
   */
  private handleEmailChanged() {
    const modalRef = this.appService.alert(this._translate.instant("account.email_changed_success"));
    modalRef.closed.pipe(take(1)).subscribe(() => this.performLogoutAfterEmailChange());
    modalRef.dismissed.pipe(take(1)).subscribe(() => this.performLogoutAfterEmailChange());
  }

  private performLogoutAfterEmailChange() {
    this.appService.logout().subscribe({
      next: () => {
        this.router.navigate(["/logout"]);
      },
      error: (error) => {
        this.appService.alertError(error);
      },
    });
  }

  public captchaModalRef;
  resolved(args) {
    this.captcha = args;
    this.captchaModalRef.close();
  }

  logout() {
    this.router.navigate(["/logout"]);
  }

  uploadDeveloperKey(fileInput: HTMLInputElement) {
    const file = fileInput.files && fileInput.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const pem = String(reader.result || "");
      this.uploadingDeveloperKey = true;
      this.developerKeyMessage = "";
      this.userService.uploadDeveloperKey(pem).subscribe({
        next: (result: any) => {
          this.uploadingDeveloperKey = false;
          this.developerKeyError = !(result && result.success);
          this.developerKeyMessage = this._translate.instant(this.developerKeyError ? "account.public_key_upload_failed" : "account.public_key_uploaded");
          fileInput.value = "";
        },
        error: () => {
          this.uploadingDeveloperKey = false;
          this.developerKeyError = true;
          this.developerKeyMessage = this._translate.instant("account.public_key_upload_failed");
          fileInput.value = "";
        },
      });
    };
    reader.readAsText(file);
  }
}
